import React from 'react';
import { Globe, ShoppingCart, Layout, Smartphone, ArrowUpRight, Check } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { AnimateOnScroll } from '../../hooks/useScrollAnimation';

const iconMap = {
  Globe,
  ShoppingCart,
  Layout,
  Smartphone
};

const colorMap = {
  'mid-blue': { bg: 'bg-mid-blue/10', text: 'text-mid-blue' },
  'mid-purple': { bg: 'bg-mid-purple/10', text: 'text-mid-purple' },
  'mid-orange': { bg: 'bg-mid-orange/10', text: 'text-mid-orange' },
  'mid-green': { bg: 'bg-mid-green/10', text: 'text-mid-green' }
};

const ServicesSection = () => {
  const { t } = useLanguage();

  const services = [
    { id: 1, titleKey: 'webdesign', descKey: 'webdesignDesc', icon: 'Globe', color: 'mid-blue' },
    { id: 2, titleKey: 'ecommerce', descKey: 'ecommerceDesc', icon: 'ShoppingCart', color: 'mid-purple' },
    { id: 3, titleKey: 'webapps', descKey: 'webappsDesc', icon: 'Layout', color: 'mid-orange' },
    { id: 4, titleKey: 'mobile', descKey: 'mobileDesc', icon: 'Smartphone', color: 'mid-green' }
  ];

  return (
    <section id="services" className="py-24 lg:py-32 bg-white dark:bg-gray-900 relative overflow-hidden">
      {/* Background */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-light-pink/10 dark:bg-mid-purple/5 rounded-full blur-hero" />
      
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
        {/* Section Header */}
        <div className="text-center mb-16">
          <AnimateOnScroll animation="fade-up">
            <span className="inline-block bg-gradient-to-r from-mid-purple/20 to-mid-blue/20 text-mid-purple px-4 py-1.5 rounded-full text-xs font-mono uppercase tracking-wider mb-4 border border-mid-purple/20">
              {t('services.badge')}
            </span>
          </AnimateOnScroll>
          <AnimateOnScroll animation="fade-up" delay={0.1}>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-black dark:text-white mb-4">
              {t('services.title')} <span className="gradient-text">{t('services.titleHighlight')}</span>
            </h2>
          </AnimateOnScroll>
          <AnimateOnScroll animation="fade-up" delay={0.2}>
            <p className="text-dark-grey dark:text-gray-400 text-lg max-w-2xl mx-auto">
              {t('services.subtitle')}
            </p>
          </AnimateOnScroll>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((service, index) => {
            const IconComponent = iconMap[service.icon];
            const colors = colorMap[service.color];
            return (
              <AnimateOnScroll 
                key={service.id} 
                animation="fade-up" 
                delay={0.1 * index}
              >
                <div className="group p-8 bg-white dark:bg-gray-800 border border-grey/20 dark:border-gray-700 rounded-2xl card-hover cursor-pointer h-full">
                  <div className={`w-16 h-16 rounded-2xl ${colors.bg} flex items-center justify-center mb-6 group-hover:scale-105 transition-transform duration-300`}>
                    {IconComponent && <IconComponent size={28} className={colors.text} />}
                  </div>
                  <h3 className="text-xl font-semibold text-black dark:text-white mb-3 flex items-center gap-2">
                    {t(`services.${service.titleKey}`)}
                    <ArrowUpRight size={16} className="opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </h3>
                  <p className="text-dark-grey dark:text-gray-400 text-sm leading-relaxed">
                    {t(`services.${service.descKey}`)}
                  </p>
                </div>
              </AnimateOnScroll>
            );
          })}
        </div>

        {/* Features List */}
        <AnimateOnScroll animation="fade-up" delay={0.3}>
          <div className="mt-16 flex flex-wrap justify-center gap-4">
            {t('services.features').map((feature) => (
              <div
                key={feature}
                className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 rounded-full border border-grey/20 dark:border-gray-700 hover:border-mid-purple/30 transition-colors"
              >
                <Check size={14} className="text-mid-green" />
                <span className="text-sm text-dark-grey dark:text-gray-400">{feature}</span>
              </div>
            ))}
          </div>
        </AnimateOnScroll>
      </div>
    </section>
  );
};

export default ServicesSection;
